import mean from 'lodash/mean'

import { generateHtmlFromJSON } from '../../../utils/functions/htmlGeneratorFromJSON'

import { getSubstringCount } from '@pepper/utils/src/function/string'

import type {
  Keyword,
  ResearchReportDocument,
} from '@pepper/utils/src/types/seo'
import type { EditorState } from 'remirror'

export const DOC_GRADE_LS_KEY_PREFIX = 'DOC_GRADE_'

export function getContentGradeElementCount(editorState: EditorState['doc']) {
  const content = editorState.toJSON()
  const html = generateHtmlFromJSON(content)
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const paragraphs = Array.from(doc.querySelectorAll('body > p')).filter(item =>
    item?.textContent?.trim()
  )
  const heading = doc.querySelectorAll('h1, h2, h3, h4, h5, h6')
  const images = doc.querySelectorAll('img')
  const links = doc.querySelectorAll('a')

  const result: Record<string, number> = {
    p: paragraphs.length,
    hx: heading.length,
    img: images.length,
    a: links.length,
  }
  return result
}

export function getKeywordPresenceArray(
  editorState: EditorState,
  keywords: { primary: Keyword[]; secondary: Keyword[]; terms: Keyword[] }
) {
  const content = editorState.doc.toJSON()
  const html = generateHtmlFromJSON(content)
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const textContent = doc.body.innerHTML

  let headingTextContent = ''
  doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(item => {
    headingTextContent += `<hx>${item.textContent}</hx> `
  })

  const headingKeyword: Keyword[] = []
  const primaryKeywordCount = keywords.primary.map(item => {
    if (item.heading_presence) headingKeyword.push(item)
    return getSubstringCount(textContent, item.keyword)
  })
  const secondaryKeywordCount = keywords.secondary.map(item => {
    if (item.heading_presence) headingKeyword.push(item)
    return getSubstringCount(textContent, item.keyword)
  })
  const termsKeywordCount = keywords.terms.map(item => {
    if (item.heading_presence) headingKeyword.push(item)
    return getSubstringCount(textContent, item.keyword)
  })
  const headingPresenceCount = headingKeyword.map(item => {
    if (getSubstringCount(headingTextContent, item.keyword)) return 1
    return 0
  })

  return {
    primary: primaryKeywordCount,
    secondary: secondaryKeywordCount,
    recommended: termsKeywordCount,
    heading_presence: headingPresenceCount,
  }
}

export function generateGradeObject(data: ResearchReportDocument) {
  return {
    document_length: Math.floor(
      mean([data.page_length.min, data.page_length.max])
    ),
    readability_grade: Math.floor(
      mean([data.readability_ease.min, data.readability_ease.max])
    ),
    headings: Math.floor(data.element_counts.hx.mean),
    paragraph_density: Math.floor(data.element_counts.p.mean),
    image_count: Math.floor(data.element_counts.img.mean),
    link_tally: Math.floor(data.element_counts.a.mean),
    keyword_optimization: {
      primary: keywordCountToArray(
        data.primary_keyword ? [data.primary_keyword] : null
      ),
      secondary: keywordCountToArray(data.secondary_keywords),
      recommended: keywordCountToArray(data.terms),
      heading_presence: keywordHeadingPresenceToArray([
        data.primary_keyword,
        ...(data?.secondary_keywords ?? []),
        ...(data?.terms ?? []),
      ]),
    },
  }
}

// convert `Keyword[]` into array of mean(min,max) values
// eg: [{min: 3, max: 4, keyword: test: heading_presence: 1}] -> [mean(3,4)]
// also handle case where count should be at least 1 -> [{min: 0, max: 1, keyword: test: heading_presence: 1}] -> [Math.max(mean(0,1), 1)]
function keywordCountToArray(keywords: Keyword[] | null) {
  return (
    keywords?.map(keyword =>
      Math.floor(Math.max(mean([keyword.min, keyword.max]), 1))
    ) ?? []
  )
}

// filter out heading `Keyword`
// eg: [{min: 3, max: 4, keyword: test: heading_presence: 1}, {min: 3, max: 4, keyword: new: heading_presence: 0}] -> [1]
function keywordHeadingPresenceToArray(keywords: (Keyword | null)[]) {
  return keywords.filter(keyword => keyword?.heading_presence).map(_ => 1)
}
